import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, BookOpenIcon, ClockIcon, StarIcon } from 'lucide-react';
import ArticleCard from '../../components/Articles/ArticleCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { useDarkMode } from '../../context/DarkModeContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface Article {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
  featured?: boolean;
}
const fantasyArticles: Article[] = [{
  id: 1,
  title: 'World-Building Techniques from Top Fantasy Authors',
  excerpt: 'Learn how bestselling authors craft immersive fantasy worlds — from magic systems to political economies — without overwhelming the reader.',
  author: 'Daniel Morgan',
  date: 'October 7, 2023',
  category: 'Craft',
  readTime: '11 min read',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  featured: true
}, {
  id: 2,
  title: 'Science Fiction Trends: AI and Beyond',
  excerpt: 'How contemporary sci-fi is tackling artificial intelligence, climate change, and post-humanism in ways that feel urgent and grounded.',
  author: 'Olivia Wu',
  date: 'October 5, 2023',
  category: 'Trends',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1501786223405-6d024d7c3b8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 3,
  title: 'The Rise of Dark Fantasy in Indie Publishing',
  excerpt: "Dark fantasy has exploded in indie markets. We look at what's driving the trend, which subgenres are growing fastest, and what readers want next.",
  author: 'Marcus Webb',
  date: 'September 28, 2023',
  category: 'Trends',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1514539079130-25950c84af65?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 4,
  title: "Science Fiction's New Wave: Climate and Identity",
  excerpt: 'Contemporary indie sci-fi is grappling with climate futures and identity politics in ways that feel urgent and deeply human. A genre survey.',
  author: 'Dr. Aiden Park',
  date: 'September 19, 2023',
  category: 'Analysis',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 5,
  title: 'Magic Systems: Hard vs. Soft Magic',
  excerpt: 'Understanding the spectrum of magic systems and how to choose the right one for your story structure and tone.',
  author: 'Elena Vance',
  date: 'September 12, 2023',
  category: 'Craft',
  readTime: '12 min read',
  image: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 6,
  title: 'Space Opera Resurgence',
  excerpt: 'Why grand-scale space adventures are making a comeback, and how indie authors are revitalizing the genre with diverse casts and modern themes.',
  author: 'Sci-Fi Weekly',
  date: 'September 8, 2023',
  category: 'Trends',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const CATEGORIES = ['All', 'Craft', 'Trends', 'Analysis', 'Marketing', 'Interviews'];
const READ_TIMES = ['All', 'Under 8 min', '8–12 min', '12+ min'];
const parseMinutes = (readTime: string): number => {
  const match = readTime.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
};
const FantasySciFiArticles = () => {
  const {
    darkMode
  } = useDarkMode();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeReadTime, setActiveReadTime] = useState('All');
  const featured = fantasyArticles.find(a => a.featured)!;
  const rest = fantasyArticles.filter(a => !a.featured);
  const filtered = useMemo(() => {
    return rest.filter(article => {
      const matchesSearch = !searchQuery || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || article.author.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
      const mins = parseMinutes(article.readTime);
      const matchesReadTime = activeReadTime === 'All' || activeReadTime === 'Under 8 min' && mins < 8 || activeReadTime === '8–12 min' && mins >= 8 && mins <= 12 || activeReadTime === '12+ min' && mins > 12;
      return matchesSearch && matchesCategory && matchesReadTime;
    });
  }, [searchQuery, activeCategory, activeReadTime, rest]);
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    value: activeCategory,
    onChange: val => setActiveCategory(val),
    options: CATEGORIES.map(c => ({
      value: c,
      label: c
    }))
  }, {
    id: 'readtime',
    title: 'Read Time',
    type: 'radio',
    value: activeReadTime,
    onChange: val => setActiveReadTime(val),
    options: READ_TIMES.map(r => ({
      value: r,
      label: r
    }))
  }];
  const handleReset = () => {
    setActiveCategory('All');
    setActiveReadTime('All');
    setSearchQuery('');
  };
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} data-id="element-16433" data-mp="2t67e01chyxvy">
      {/* Header */}
      <div className="mb-8" data-id="element-16434" data-mp="2qtn4ltgoy6us">
        <div className="flex items-center gap-2 mb-2" data-id="element-16435" data-mp="2mqqgxocankjz">
          <BookOpenIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-16436" data-mp="2mp5mdypzc1m4" />
          <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16437" data-mp="2hqubdkny7l2b">
            The Trove
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16438" data-mp="2ctugm5wju4qi" />
          <Link to="/trove/fantasy-scifi" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16439" data-mp="3agt53td386db">
            Fantasy & Sci-Fi
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16440" data-mp="35jknbtvuq4cl" />
          <span className={`text-sm ${dk.textMuted}`} data-id="element-16441" data-mp="30ox7ejewyi9m">Articles</span>
        </div>
        <div className="flex items-center gap-3 mb-3" data-id="element-16442" data-mp="2vqdfm2dn2c9c">
          <StarIcon className="h-8 w-8 text-blue-500" data-id="element-16443" data-mp="3t6sr27dmt5ci" />
          <h1 className={`text-3xl sm:text-4xl font-bold ${dk.textPrimary}`} data-id="element-16444" data-mp="3t8dllwzy4oad">
            Fantasy & Sci-Fi Articles
          </h1>
        </div>
        <p className={`text-base max-w-2xl mb-6 ${dk.textMuted}`} data-id="element-16445" data-mp="3vr8nc9arjf1x">
          Explore world-building, magic systems, speculative futures, and the
          latest trends in SFF publishing.
        </p>
        <div className="max-w-md" data-id="element-16446" data-mp="9k0nfmim40da">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search SFF articles..." variant="pill" suggestions={[]} data-id="element-16447" data-mp="9iz7b3mqonop" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-16448" data-mp="iiuobp2z7cwn">
        {/* Featured Article */}
        {!searchQuery && activeCategory === 'All' && activeReadTime === 'All' && <section data-id="element-16449" data-mp="sb0csou0rc73">
              <h2 className={`text-lg font-semibold mb-4 ${dk.textPrimary}`} data-id="element-16450" data-mp="scupxgz1u00m">
                Featured
              </h2>
              <article className={`${dk.cardSubtle} rounded-xl shadow-sm border overflow-hidden`} data-id="element-16451" data-mp="128y3dizcpl2v">
                <div className="md:flex" data-id="element-16452" data-mp="16bb4fu35kutb">
                  <div className="md:w-2/5" data-id="element-16453" data-mp="167x38p686rng">
                    <img src={featured.image} alt={featured.title} className="w-full h-52 md:h-full object-cover" data-id="element-16454" data-mp="1rkgfu8ramwzz" />
                  </div>
                  <div className="md:w-3/5 p-6 flex flex-col justify-between" data-id="element-16455" data-mp="21ivzjqnzr3zg">
                    <div data-id="element-16456" data-mp="1wlcm2sz4av8k">
                      <div className="flex items-center gap-2 mb-3" data-id="element-16457" data-mp="1wl462s7p79t5">
                        <span className={`px-2.5 py-0.5 text-white text-xs font-medium rounded-full ${rt.badgeBg}`} data-id="element-16458" data-mp="18vrz7yzufdu0">
                          {featured.category}
                        </span>
                        <span className={`text-xs ${dk.textMuted}`} data-id="element-16459" data-mp="1imcx1k7p0ebv">
                          {featured.date}
                        </span>
                      </div>
                      <h3 className={`text-xl font-bold mb-2 leading-snug ${dk.textPrimary}`} data-id="element-16460" data-mp="q2o4096z8sxa">
                        {featured.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-3 ${dk.textSecondary}`} data-id="element-16461" data-mp="2wr6kvh4jgvyj">
                        {featured.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-16462" data-mp="2rteift69prg3">
                      <div className="text-xs" data-id="element-16463" data-mp="2rto8fgrbbyls">
                        <p className={`font-semibold ${dk.textHeading}`} data-id="element-16464" data-mp="3fihbdx6cr6pw">
                          {featured.author}
                        </p>
                        <p className={`flex items-center gap-1 mt-0.5 ${dk.textMuted}`} data-id="element-16465" data-mp="3khap9qpjwzql">
                          <ClockIcon className="h-3 w-3" data-id="element-16466" data-mp="3bh5gbc4pltab" />
                          {featured.readTime}
                        </p>
                      </div>
                      <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16467" data-mp="1v1uey7zw57z4">
                        Read full article →
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </section>}

        {/* Category quick-filters (inline chips) */}
        <div className="flex flex-wrap gap-2" data-id="element-16468" data-mp="2afoarkbqpk1m">
          {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${activeCategory === cat ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-700' : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-16469" data-mp="2feiqgdeeushi">
              {cat}
            </button>)}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between" data-id="element-16470" data-mp="3qv9066i1m2fv">
          <p className={`text-sm ${dk.textMuted}`} data-id="element-16471" data-mp="3qsmsm6v5jmxn">
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            {searchQuery ? ` matching "${searchQuery}"` : ''}
          </p>
          {(activeCategory !== 'All' || activeReadTime !== 'All' || searchQuery) && <button onClick={handleReset} className={`text-xs font-medium ${rt.accentLink}`} data-id="element-16472" data-mp="8tae31ofw5ar">
              Clear filters
            </button>}
        </div>

        {/* Articles Grid */}
        {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-16473" data-mp="wjellk4r0p71">
            {filtered.map(article => <ArticleCard key={article.id} article={article} data-id="element-16474" data-mp="sjm7yt0hzsut" />)}
          </div> : <div className={`text-center py-16 rounded-xl border border-dashed ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`} data-id="element-16475" data-mp="2yc2nnq5np6l8">
            <BookOpenIcon className={`h-8 w-8 mx-auto mb-3 opacity-30 ${dk.textMuted}`} data-id="element-16476" data-mp="xb4jszqnh02h" />
            <p className={`text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-16477" data-mp="128wjifxjdqh8">
              No articles found
            </p>
            <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-16478" data-mp="1c6u9p8yyhxch">
              Try adjusting your filters or search term
            </p>
            <button onClick={handleReset} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16479" data-mp="1g97ffxdpdv99">
              Clear all filters
            </button>
          </div>}
      </div>
    </TroveLayout>;
};
export default FantasySciFiArticles;